import React, { useState } from "react";

import CompanyLocationForm from "components/CompanyLocations/CompanyLocationForm";
import type { ProposedRoute } from "types/routeBuilder";
import { useRouteBuilder } from "pages/Admin/Routes/RouteBuilder/context/RouteBuilderContext";
import LocationSelect from "pages/Admin/Routes/RouteBuilder/components/SetupRouteBuilderModal/LocationSelect";

type EndpointType = "start" | "end";

const TITLES: Record<EndpointType, string> = {
  start: "Start Location",
  end: "End Location",
};

export default function RouteEndpointsEditor({
  route,
}: {
  route: ProposedRoute;
}) {
  const { locations, isSavingLocation, saveCompanyLocation, setRouteEndpoint } =
    useRouteBuilder();
  const [addingFor, setAddingFor] = useState<EndpointType | null>(null);

  // With no saved locations the dropdown has nothing to offer, so open the
  // address form for the first endpoint still missing a location.
  const autoAdding: EndpointType | null =
    locations.length > 0
      ? null
      : !route.startLocation.location
        ? "start"
        : !route.endLocation.location
          ? "end"
          : null;
  const adding = addingFor ?? autoAdding;

  const choose = (
    type: EndpointType,
    location: Parameters<typeof setRouteEndpoint>[2],
  ) => {
    setRouteEndpoint(route.id, type, location);
    setAddingFor(null);
  };

  return (
    <div
      className="mt-2 border-t border-slate-100 pt-2"
      data-testid={`route-endpoints-${route.id}`}
    >
      {(["start", "end"] as const).map((type) => (
        <div
          key={type}
          className={
            type === "start"
              ? "mb-1.5 border-b border-slate-200 pb-2"
              : undefined
          }
        >
          <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-slate-500">
            <span className="w-8 shrink-0 font-semibold capitalize text-slate-600">
              {type}
            </span>
            <LocationSelect
              compact
              label={TITLES[type]}
              testId={`${type}-location-select-${route.id}`}
              locations={locations}
              selected={route[`${type}Location`]}
              addingAddress={adding === type}
              disabled={isSavingLocation}
              onSelect={(next) => choose(type, next)}
              onNewAddress={() => setAddingFor(type)}
            />
          </div>
          {adding === type ? (
            <div
              className="mt-2 rounded-lg border border-sky-200 bg-white p-2"
              data-testid={`new-${type}-location-panel-${route.id}`}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-700">
                  {TITLES[type]}
                </p>
                {addingFor ? (
                  <button
                    type="button"
                    className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100"
                    aria-label={`Close ${TITLES[type].toLowerCase()} form`}
                    onClick={() => setAddingFor(null)}
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <CompanyLocationForm
                mode="create"
                compact
                saveLabel="Save"
                saving={isSavingLocation}
                onSave={async (values) => {
                  const saved = await saveCompanyLocation(values);
                  if (saved) choose(type, saved);
                }}
                onCancel={addingFor ? () => setAddingFor(null) : undefined}
              />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
